// hooks/useEspera.ts - Proteções da espera da Dinâmica (D78, D80; entrega 1.6).
//
// A geração não termina se o professor sai da tela (D78). Enquanto prepara: a tela do celular fica acesa
// (Screen Wake Lock API) e sair pede confirmação, tanto pelos links do shell quanto pelo voltar do celular;
// fechar ou recarregar a aba usa o aviso do próprio navegador. A geração em si não muda.

import { useEffect, useRef, useState } from 'react';

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

/** Mantém a tela acesa enquanto `ativo`. Sem suporte no navegador, não faz nada. */
export function useTelaAcesa(ativo: boolean): void {
  useEffect(() => {
    if (!ativo) return;
    const wl = (navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<WakeLockSentinelLike> } }).wakeLock;
    if (!wl) return;
    let sentinela: WakeLockSentinelLike | null = null;
    let vivo = true;
    const pedir = () => {
      wl.request('screen')
        .then((s) => {
          if (vivo) sentinela = s;
          else void s.release();
        })
        .catch(() => undefined);
    };
    pedir();
    // O navegador solta a trava quando a aba some; ao voltar, pede de novo.
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') pedir();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', aoVoltar);
      void sentinela?.release().catch(() => undefined);
    };
  }, [ativo]);
}

/**
 * Enquanto `ativo`, sair pede confirmação: `pedir(sair)` abre o diálogo da tela e chama `sair()` se o
 * professor confirmar.
 */
export function useSaidaProtegida(ativo: boolean, pedir: (sair: () => void) => void): void {
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;

  useEffect(() => {
    if (!ativo) return;
    let protegido = true;

    const aoFechar = (e: BeforeUnloadEvent) => {
      if (!protegido) return;
      e.preventDefault();
      e.returnValue = '';
    };

    // Links (o shell, o caminho, o perfil): o clique para antes de navegar, e o diálogo decide.
    const aoClicar = (e: MouseEvent) => {
      if (!protegido || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      e.preventDefault();
      const destino = a.href;
      pedirRef.current(() => {
        protegido = false;
        window.location.assign(destino);
      });
    };

    // Voltar do celular ou do navegador: fica uma entrada a mais no histórico; ao voltar, ela é reposta
    // e o diálogo pergunta. Rolagem manual antes da entrada extra: ao tirá-la, o navegador não devolve a
    // rolagem de quando a espera começou (a tela seguinte já abriu no topo).
    const restauracao = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.history.pushState({ esperaDinamica: true }, '');
    const aoVoltar = () => {
      if (!protegido) return;
      window.history.pushState({ esperaDinamica: true }, '');
      pedirRef.current(() => {
        protegido = false;
        window.history.go(-2);
      });
    };

    window.addEventListener('beforeunload', aoFechar);
    document.addEventListener('click', aoClicar, true);
    window.addEventListener('popstate', aoVoltar);
    return () => {
      document.removeEventListener('click', aoClicar, true);
      window.removeEventListener('beforeunload', aoFechar);
      window.removeEventListener('popstate', aoVoltar);
      // Espera terminada: tira a entrada extra do histórico (mesmo endereço, sem navegar) e, de volta à
      // entrada de antes, repõe a rolagem do navegador.
      const repor = () => {
        window.history.scrollRestoration = restauracao;
      };
      if (protegido && window.history.state?.esperaDinamica) {
        window.addEventListener('popstate', repor, { once: true });
        window.history.back();
      } else {
        repor();
      }
    };
  }, [ativo]);
}

/** Segundos desde que a espera começou, atualizados a cada segundo. */
export function useSegundos(ativo: boolean): number {
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    if (!ativo) {
      setSegundos(0);
      return;
    }
    const inicio = Date.now();
    const id = window.setInterval(() => setSegundos(Math.floor((Date.now() - inicio) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [ativo]);
  return segundos;
}
